import { HaveFinish } from 'utils/finishes';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../baseQuery';
import { SetDetail } from '../../trade/tradeApi';

/**
 * A single trader who wants this card (any printing sharing the oracle id), already
 * filtered server-side to those who can afford their requested printing, with the
 * viewer and anyone who has the viewer blocked excluded.
 *
 * Field names/types mirror TradeAlertRequester + TradeAlertCard
 * (store/services/tradeAlerts/tradeAlertsApi.ts) so the Send flow can reuse the same
 * ComposeTradeModal staging logic.
 */
export interface CardWanter {
  // --- Trader identity (parity with TradeAlertRequester) ---
  userId: number;
  username: string;
  avatarUrl?: string | null;
  isElite?: boolean;
  showAnimatedBorder?: boolean;
  borderColor1?: string | null;
  borderColor2?: string | null;
  country?: string | null; // country code → flag
  points?: number; // trader's balance (hover-card context)

  // --- The requested printing (parity with TradeAlertCard) ---
  wantListItemId: number;
  cardId: number; // numeric printing id the wanter wants
  scryfallId?: string;
  setCode: string;
  setName?: string;
  collectorNumber?: string;
  imageUrl?: string;
  quantity: number;
  condition: string; // Prisma enum e.g. "NEAR_MINT"
  language: string; // lowercase Scryfall code e.g. "en"
  foil: boolean;
  // Requested printing's point value (Points column + default sort key). Read it
  // through hasRealPrice: null or 0 means the printing has no price.
  pointValue: number | null;

  // --- Acceptance criteria so ComposeTradeModal can stage the exact printing ---
  acceptableSetDetails?: SetDetail[];
  acceptableConditions?: string[];
  acceptableLanguages?: string[];
  acceptFoil?: boolean;
  acceptNonFoil?: boolean;
  availableInFoil?: boolean;
  availableInNonFoil?: boolean;
  /** Track D: the requested printing's finishes (lowercase) and the want's etched intent */
  availableFinishes?: string[] | null;
  acceptEtched?: boolean | null;
  finish?: HaveFinish | null;
  availableLanguages?: Array<{ lang: string; scryfallId: string; setCode?: string }>;
}

export interface GetCardWantersResponse {
  /** Already filtered to affordable + de-blocked + self-excluded. */
  wanters: CardWanter[];
  /**
   * Count of wanters of any printing BEFORE the affordability/block/self filter.
   * Lets the UI distinguish "no wanters at all" (hide the table) from
   * "wanters exist but none affordable" (show the empty-state row).
   */
  totalWanters: number;
}

/**
 * PUBLIC endpoint (works logged-out) for the "Traders who want this card" table on the
 * Card Details page. `requiresAuth: false` is deliberate: baseQuery defaults that flag to
 * `true` when omitted, which would force a logout on any 401 — undesirable on a public
 * card page. With it false, a transient 401 never logs the viewer out. The session cookie
 * is still sent (credentials: 'include' is unconditional in cookie-auth mode), so the
 * backend can still self-exclude a logged-in viewer from their own wanters.
 */
export const cardWantersApi = createApi({
  reducerPath: 'cardWantersApi',
  baseQuery: baseQuery,
  tagTypes: ['CardWanters'],
  endpoints: (builder) => ({
    getCardWanters: builder.query<GetCardWantersResponse, { oracleId: string }>({
      query: ({ oracleId }) => ({
        url: `/cards/${oracleId}/wanters`,
        method: 'GET',
        requiresAuth: false,
      }),
      providesTags: ['CardWanters'],
    }),
  }),
});

export const { useGetCardWantersQuery } = cardWantersApi;
